import React from 'react';

/**
 * Catches a render error so it costs a panel rather than the whole app.
 *
 * There was no boundary anywhere. React's behaviour without one is to unmount
 * the entire tree, so any component throwing — a null where an object was
 * expected, a bad shape from Firestore, a typo in a rarely-taken branch — left
 * a white screen with no message, no way back, and nothing in the UI to say
 * what happened. The user's only recourse was to guess that a reload might
 * help.
 *
 * This session shipped exactly that class of bug (a component declared inside
 * a render body, remounting its subtree on every keystroke), which is the
 * argument for having one: the question is not whether a render error happens
 * but what the user sees when it does.
 *
 * Deliberately not clever. It shows what broke, offers the two things that
 * actually help, and stays out of the way.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The console is where a developer will look, and the only place this can
    // reliably be recorded without inventing an error-reporting pipeline.
    console.error('[ErrorBoundary]', this.props.label || 'app', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="error-boundary">
        <div className="error-boundary-icon">⚠</div>
        <h2 className="error-boundary-title">
          {this.props.label ? `${this.props.label} could not be shown` : 'Something went wrong'}
        </h2>
        <p className="error-boundary-body">
          This part of the app hit an error. Your data is unaffected — nothing was saved or
          changed by this.
        </p>
        <div className="error-boundary-actions">
          {/* Trying again is free and often enough: a transient failure loading
              data will not recur. */}
          <button className="error-boundary-btn" onClick={() => this.setState({ error: null })}>
            Try again
          </button>
          <button className="error-boundary-btn secondary" onClick={() => window.location.reload()}>
            Reload the app
          </button>
        </div>
        <details className="error-boundary-detail">
          <summary>What went wrong</summary>
          <pre>{String(error?.message || error)}</pre>
        </details>
      </div>
    );
  }
}
